import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  FormSettingModel, PostSubmitAction, FormStepConfig,
  StepRoutingRule, StepRouteOperator,
} from '../../../models/form.model';
import { FormFieldModel } from '../../../models/form-field.model';
import { parseStepsConfig, parseStepRoutingConfig } from '../../../utils/step-routing';

@Component({
  selector: 'app-form-settings-panel',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './form-settings-panel.component.html',
})
export class FormSettingsPanelComponent implements OnChanges {
  @Input() settings: FormSettingModel | null = null;
  @Input() saving = false;
  @Input() fields: FormFieldModel[] = []; // inc23: para elegir el campo disparador
  @Output() saveChanges = new EventEmitter<Partial<FormSettingModel>>();

  readonly postSubmitActions: { value: PostSubmitAction; label: string }[] = [
    { value: 'MESSAGE', label: 'Mostrar mensaje' },
    { value: 'REDIRECT', label: 'Redirigir' },
    { value: 'BOTH', label: 'Mensaje + redirección' },
  ];

  // inc22: config por paso (título/descr/imagen).
  steps: FormStepConfig[] = [];
  // inc23: reglas de salto condicional entre pasos.
  routingRules: StepRoutingRule[] = [];

  readonly routeOperators: { value: StepRouteOperator; label: string }[] = [
    { value: 'equals',     label: 'es igual a' },
    { value: 'not_equals', label: 'no es igual a' },
    { value: 'contains',   label: 'contiene' },
    { value: 'not_empty',  label: 'no está vacío' },
    { value: 'is_empty',   label: 'está vacío' },
  ];

  form!: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      isMultiStep: [false],
      totalSteps: [1],
      styleConfig: [''],
      submitButtonLabel: [''],
      hideProviderBranding: [false],

      postSubmitAction: ['MESSAGE' as PostSubmitAction],
      postSubmitMessage: [''],
      postSubmitMessageKnown: [''],
      postSubmitRedirectUrl: [''],
      postSubmitRedirectDelayS: [0],

      confirmEmailEnabled: [false],
      confirmEmailSubject: [''],
      confirmEmailBody: [''],
      confirmEmailAttachmentUrl: [''],

      notifyTeamEnabled: [false],
      notifyTeamEmails: [''],
      doubleOptin: [false],

      triggerDelaySeconds: [0],
      triggerScrollPct: [0],
      triggerExitIntent: [false],
      triggerClickSelector: [''],
      triggerFrequency: [''],
      triggerAudienceRules: [''],
      gdprConfig: [''],       // raw JSON from backend — not shown in UI, used to hydrate the two fields below
      gdprPrivacyUrl: [''],   // URL de política de privacidad (form-level)
      gdprLinkText: [''],     // Texto del enlace de privacidad
      gdprPolicyText: [''],   // Texto de política escrito directamente (abre modal)

      retentionMonths: [24],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings'] && this.settings) {
      this.form.patchValue(this.settings, { emitEvent: false });
      // Estas columnas son JSONB en la BD. El backend las devuelve como string JSON
      // (p. ej. {"consentText":"..."} / {"countries":[...]}); el usuario, en cambio,
      // escribe texto plano en la UI. Desenvolvemos para mostrar el texto legible.
      const gdpr = this.unwrapGdprConfig(this.settings.gdprConfig);
      this.form.patchValue({
        gdprPrivacyUrl: gdpr.privacyUrl,
        gdprLinkText: gdpr.linkText,
        gdprPolicyText: gdpr.policyText,
        triggerAudienceRules: this.unwrapCountries(this.settings.triggerAudienceRules),
      }, { emitEvent: false });
      this.steps = parseStepsConfig(this.settings.stepsConfig);
      this.routingRules = parseStepRoutingConfig(this.settings.stepRoutingConfig)?.rules ?? [];
      this.syncStepsLength();
    }
  }

  // ── JSONB helpers ───────────────────────────────────────────────────────────
  // Los campos gdprConfig / triggerAudienceRules / styleConfig se persisten en
  // columnas JSONB. Enviar texto plano o cadena vacía a JSONB provoca 22P02 (HTTP
  // 500). Por eso al guardar convertimos a objeto JSON válido o null, y al cargar
  // desenvolvemos para que el usuario vea/edite texto plano.

  private unwrapGdprConfig(raw: string | null | undefined): { privacyUrl: string; linkText: string; policyText: string } {
    if (!raw) return { privacyUrl: '', linkText: '', policyText: '' };
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return {
        privacyUrl: typeof parsed['privacyUrl'] === 'string' ? parsed['privacyUrl'] : '',
        linkText:   typeof parsed['linkText']   === 'string' ? parsed['linkText']   : '',
        policyText: typeof parsed['policyText'] === 'string' ? parsed['policyText'] : '',
      };
    } catch {
      return { privacyUrl: '', linkText: '', policyText: '' };
    }
  }

  private unwrapCountries(raw: string | null | undefined): string {
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.countries)) {
        return parsed.countries.join(', ');
      }
      if (typeof parsed === 'string') return parsed;
    } catch {
      return raw;
    }
    return raw;
  }

  /** {privacyUrl, linkText, policyText} → JSON válido para JSONB, o null si no hay ni URL ni texto. */
  private wrapGdprConfig(privacyUrl: unknown, linkText: unknown, policyText: unknown): string | null {
    const url  = typeof privacyUrl === 'string' ? privacyUrl.trim()  : '';
    const text = typeof linkText   === 'string' ? linkText.trim()    : '';
    const body = typeof policyText === 'string' ? policyText.trim()  : '';
    if (!url && !body) return null;
    return JSON.stringify({
      ...(url  ? { privacyUrl: url } : {}),
      linkText: text || 'Política de privacidad',
      ...(body ? { policyText: body } : {}),
    });
  }

  /** "PE, MX, CO" → {"countries":["PE","MX","CO"]} JSON válido, o null si vacío. */
  private wrapCountries(text: unknown): string | null {
    const list = (typeof text === 'string' ? text : '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    return list.length ? JSON.stringify({ countries: list }) : null;
  }

  /** styleConfig: sin UI de edición; cadena vacía → null, JSON existente se conserva. */
  private normalizeStyleConfig(raw: unknown): string | null {
    const t = typeof raw === 'string' ? raw.trim() : '';
    return t ? t : null;
  }

  // ── Multi-paso (inc22) ──────────────────────────────────────────────────────

  get totalSteps(): number {
    return Number(this.form.value.totalSteps) || 1;
  }

  get stepNumbers(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  /** Garantiza un objeto de config para cada paso hasta totalSteps. */
  syncStepsLength(): void {
    const total = this.totalSteps;
    const next: FormStepConfig[] = [];
    for (let i = 1; i <= total; i++) {
      next.push(this.steps.find(s => s.step === i) ?? { step: i, title: '', description: '', imageUrl: '' });
    }
    this.steps = next;
  }

  onTotalStepsChange(): void {
    this.syncStepsLength();
  }

  // ── Salto condicional (inc23) ───────────────────────────────────────────────

  // inc32: solo los campos que pertenecen al paso indicado.
  fieldsForStep(step: number): FormFieldModel[] {
    return this.fields.filter(f => (f.stepNumber ?? 1) === step);
  }

  addRoutingRule(): void {
    this.routingRules = [
      ...this.routingRules,
      {
        fromStep: 1,
        // inc32: primer campo DEL PASO, no del array global.
        fieldKey: this.fieldsForStep(1)[0]?.fieldKey ?? '',
        operator: 'equals',
        value: '',
        goToStep: Math.min(2, this.totalSteps),
      },
    ];
  }

  // inc32: al cambiar el paso de origen, el campo elegido debe pertenecer al
  // nuevo paso; si no, se reinicia al primer campo de ese paso.
  onRuleFromStepChange(rule: StepRoutingRule): void {
    const stepFields = this.fieldsForStep(rule.fromStep);
    if (!stepFields.some(f => f.fieldKey === rule.fieldKey)) {
      rule.fieldKey = stepFields[0]?.fieldKey ?? '';
    }
  }

  removeRoutingRule(index: number): void {
    this.routingRules = this.routingRules.filter((_, i) => i !== index);
  }

  routeNeedsValue(op: StepRouteOperator): boolean {
    return op !== 'not_empty' && op !== 'is_empty';
  }

  fieldLabel(fieldKey: string): string {
    return this.fields.find(f => f.fieldKey === fieldKey)?.label ?? fieldKey;
  }

  onSubmit(): void {
    if (this.saving) return;
    const raw = this.form.getRawValue() as Record<string, unknown>;
    const { gdprPrivacyUrl, gdprLinkText, gdprPolicyText, ...rest } = raw;
    const payload: Partial<FormSettingModel> = rest as Partial<FormSettingModel>;

    // Columnas JSONB: convertir a JSON válido o null antes de enviar (evita 22P02/500).
    payload.gdprConfig = this.wrapGdprConfig(gdprPrivacyUrl, gdprLinkText, gdprPolicyText);
    payload.triggerAudienceRules = this.wrapCountries(raw['triggerAudienceRules']);
    payload.styleConfig = this.normalizeStyleConfig(raw['styleConfig']);

    if (payload.isMultiStep) {
      // inc22: sólo persistimos config de pasos con algún contenido.
      const meaningful = this.steps.filter(s => s.title?.trim() || s.description?.trim() || s.imageUrl?.trim());
      payload.stepsConfig = meaningful.length ? JSON.stringify(this.steps) : '';

      // inc23: reglas de salto condicional válidas.
      const validRules = this.routingRules.filter(r => r.fieldKey && r.fromStep && r.goToStep);
      payload.stepRoutingConfig = validRules.length ? JSON.stringify({ rules: validRules }) : '';
    } else {
      // Formulario de una sola hoja: limpiamos config de pasos.
      payload.stepsConfig = '';
      payload.stepRoutingConfig = '';
    }

    this.saveChanges.emit(payload);
  }

}
